import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Client, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { API_URL } from '../../config';
import { getAuthToken } from '../api/auth';

/**
 * One live connection for the whole app, over the /ws STOMP endpoint the workshops already
 * use. Pages do not open sockets of their own: they call useRealtime(destination, handler),
 * and the provider keeps one subscription per destination however many components listen.
 *
 * Reconnects by itself, backing off from 2s to 30s so a server restart is not met by every
 * open tab hammering it, and resubscribes everything on reconnect. When the tab becomes
 * visible again after sleeping it reconnects at once rather than waiting out the backoff.
 *
 * `connected` is exposed so callers can fall back to slow polling only while the socket is
 * down, instead of polling all the time.
 */

type Handler = (payload: any) => void;

interface RealtimeApi {
  connected: boolean;
  subscribe: (destination: string, handler: Handler) => () => void;
}

const noop: RealtimeApi = { connected: false, subscribe: () => () => {} };
const RealtimeContext = createContext<RealtimeApi>(noop);

const MIN_DELAY = 2000;
const MAX_DELAY = 30000;

export function RealtimeProvider({ userId, children }: { userId?: string | null; children: React.ReactNode }) {
  const [connected, setConnected] = useState(false);
  const clientRef = useRef<Client | null>(null);
  const handlersRef = useRef(new Map<string, Set<Handler>>());
  const subsRef = useRef(new Map<string, StompSubscription>());

  const attach = useCallback((client: Client, destination: string) => {
    if (!client.connected || subsRef.current.has(destination)) return;
    const sub = client.subscribe(destination, (message) => {
      let payload: any = null;
      try {
        payload = JSON.parse(message.body);
      } catch {
        payload = message.body;
      }
      handlersRef.current.get(destination)?.forEach((handler) => {
        try {
          handler(payload);
        } catch {
          // One listener failing must not stop the others hearing it.
        }
      });
    });
    subsRef.current.set(destination, sub);
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!userId || !token) return undefined;

    let delay = MIN_DELAY;
    const client = new Client({
      webSocketFactory: () => new SockJS(`${API_URL}/ws`),
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: delay,
      onConnect: () => {
        delay = MIN_DELAY;
        client.reconnectDelay = delay;
        subsRef.current.clear();
        handlersRef.current.forEach((_, destination) => attach(client, destination));
        setConnected(true);
      },
      onWebSocketClose: () => {
        setConnected(false);
        subsRef.current.clear();
        delay = Math.min(delay * 2, MAX_DELAY);
        client.reconnectDelay = delay;
      },
      onStompError: () => setConnected(false),
    });
    clientRef.current = client;
    client.activate();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !client.connected) {
        delay = MIN_DELAY;
        client.reconnectDelay = delay;
        // Restart now instead of waiting out a backoff that grew while the tab slept.
        client.deactivate().then(() => client.activate()).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    const subs = subsRef.current;
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clientRef.current = null;
      subs.clear();
      setConnected(false);
      client.deactivate();
    };
  }, [userId, attach]);

  const subscribe = useCallback((destination: string, handler: Handler) => {
    const handlers = handlersRef.current;
    if (!handlers.has(destination)) handlers.set(destination, new Set());
    handlers.get(destination)!.add(handler);
    if (clientRef.current) attach(clientRef.current, destination);

    return () => {
      const set = handlers.get(destination);
      if (!set) return;
      set.delete(handler);
      if (set.size === 0) {
        handlers.delete(destination);
        subsRef.current.get(destination)?.unsubscribe();
        subsRef.current.delete(destination);
      }
    };
  }, [attach]);

  const api = useMemo(() => ({ connected, subscribe }), [connected, subscribe]);
  return <RealtimeContext.Provider value={api}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeStatus(): boolean {
  return useContext(RealtimeContext).connected;
}

/**
 * Calls handler with every message on destination while mounted. The latest handler is always
 * the one called, so it may close over state without resubscribing on every render. Pass a
 * null destination to listen to nothing (a page still loading its id, say).
 */
export function useRealtime(destination: string | null | undefined, handler: Handler): void {
  const { subscribe } = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!destination) return undefined;
    return subscribe(destination, (payload) => handlerRef.current(payload));
  }, [destination, subscribe]);
}
